import { Box, Flex, Spinner as ChakraSpinner } from "@chakra-ui/react";
import {
  useTrackingEvents,
  transformFiltersToUnitsSearchParams,
  useInResiStore,
} from "@superbright/indexeddb-orm";
import { useEffect, useState } from "react";

import { useFiltersAndUnits } from "api/filters/useFiltersAndUnits";
import type { FiltersResponse } from "api/schemas";
import { Icon, Typography } from "components";
import type { ListingsClientResponse } from "features/results/types/results.types";
import { useNavigate, useParams } from "shared/Router";
import { filterActions } from "store/actions";

import styles from "./QuestionnaireAllSet.module.css";

export interface QuestionnaireAllSetProps {
  isLoaded?: boolean;
  transitionDuration?: number;
  filtersData?: FiltersResponse;
  setUnitsEnabled?: (enabled: boolean) => void;
  propertyData?: { data: { id: number | string; slug: string; name: string } };
}

export const QuestionnaireAllSet = ({
  isLoaded: initialLoaded = false,
  transitionDuration = 300,
  setUnitsEnabled,
  propertyData,
}: QuestionnaireAllSetProps) => {
  const { propertySlug } = useParams();
  const navigate = useNavigate();
  const { trackQuestionnaireResult, trackQuestionnaireZeroResults } =
    useTrackingEvents();

  const analyticsPropertyId = propertyData
    ? String(propertyData.data.id)
    : null;
  const analyticsPropertySlug = propertyData?.data.slug ?? null;
  const analyticsPropertyName = propertyData?.data.name ?? null;

  // ✅ consume context from the Layout (no local queries here)
  // const { setUnitsEnabled, unitsQuery } = useOutletContext<LayoutCtx>();

  const store = useInResiStore();

  const [isLoaded, setIsLoaded] = useState(initialLoaded);
  const [spinnerVisible, setSpinnerVisible] = useState(!initialLoaded);
  const [contentVisible, setContentVisible] = useState(initialLoaded);

  const unitsPayload = transformFiltersToUnitsSearchParams(store.tempFilters);

  const { unitsQuery } = useFiltersAndUnits(
    propertySlug ?? "",
    store.tempFilters,
    {
      saveFiltersOnSuccess: true,
      unitsOptions: { enabled: true },
    }
  );

  // Resolve questionnaire → tempFilters.highlights, then mark resolved
  useEffect(() => {
    const fetchResolved = async () => {
      try {
        setUnitsEnabled?.(true); // Enable units fetch after resolving questionnaire
      } catch {
        // Failed to fetch resolved questionnaire values
      }
    };

    fetchResolved();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (unitsQuery?.isSuccess) {
      setIsLoaded(true);
      // persist filters in your stores once units have been fetched
      void (async () => {
        filterActions.set(
          unitsPayload as unknown as Parameters<typeof filterActions.set>[0]
        );
        await store.setApiFilters(unitsPayload);
      })();

      if (analyticsPropertyId && analyticsPropertySlug) {
        const totalUnits =
          unitsQuery.data.all.total +
          unitsQuery.data.bestFits.total +
          unitsQuery.data.closest.total;
        if (totalUnits === 0) {
          trackQuestionnaireZeroResults({
            propertyName: analyticsPropertyName ?? "",
            propertyId: analyticsPropertyId,
            propertySlug: analyticsPropertySlug,
            answers: store.tempFilters,
            totalUnits,
          });
        }
        trackQuestionnaireResult({
          propertyName: analyticsPropertyName ?? "",
          propertyId: analyticsPropertyId,
          propertySlug: analyticsPropertySlug,
          fullQuestionnaireSchemaJson: store.tempFilters,
          resultsJson: {
            total:
              unitsQuery.data.all.total +
              unitsQuery.data.bestFits.total +
              unitsQuery.data.closest.total,
            units: [
              ...unitsQuery.data.all.hits,
              ...unitsQuery.data.bestFits.hits,
              ...unitsQuery.data.closest.hits,
            ].map((u) => ({
              unitId: String(u.id ?? ""),
              unitName: u.name,
            })),
          },
        });
      }
    } else if (unitsQuery?.isError) {
      console.error("unitsQuery error", unitsQuery.error);
      setIsLoaded(true);
    }
  }, [unitsQuery?.isSuccess, unitsQuery?.isError]); // eslint-disable-line react-hooks/exhaustive-deps

  // Handle success→fade text, then redirect
  useEffect(() => {
    if (isLoaded) {
      setSpinnerVisible(false);

      const contentTimeout = setTimeout(() => {
        setContentVisible(true);
      }, transitionDuration);

      const redirectTimeout = setTimeout(() => {
        navigate("results");
      }, 2000);

      return () => {
        clearTimeout(contentTimeout);
        clearTimeout(redirectTimeout);
      };
    } else {
      setSpinnerVisible(true);
      setContentVisible(false);
    }
  }, [isLoaded, transitionDuration, navigate]);

  const unitsData = unitsQuery?.data as ListingsClientResponse | undefined;
  const isClosest =
    (unitsData?.closest.hits.length ?? 0) > 0 &&
    (unitsData?.bestFits.hits.length ?? 0) === 0 &&
    (unitsData?.all.hits.length ?? 0) === 0;
  const totalFound =
    (unitsData?.all.total ?? 0) +
    (unitsData?.bestFits.total ?? 0) +
    (unitsData?.closest.total ?? 0);

  return (
    <Flex
      flexDirection="column"
      width={"100%"}
      gap={6}
      flex={1}
      textAlign="center"
      justifyContent="center"
    >
      <Box
        position="relative"
        height={10}
        display="flex"
        justifyContent="center"
      >
        {/* Spinner */}
        <Box
          className={`${styles.fadeTransition} ${
            spinnerVisible ? styles.visible : styles.hidden
          }`}
          style={{
            position: "absolute",
            transitionDuration: `${transitionDuration}ms`,
          }}
        >
          <ChakraSpinner size="xl" />
        </Box>

        {/* Success Icon */}
        <Box
          className={`${styles.fadeTransition} ${
            contentVisible ? styles.visible : styles.hidden
          }`}
          style={{
            position: "absolute",
            transitionDuration: `${transitionDuration}ms`,
          }}
          display="flex"
          alignItems="center"
          justifyContent="center"
          width={10}
          height={10}
          background="var(--color-stone-600)"
          color="var(--color-white)"
          borderRadius={20}
        >
          <Icon name={isClosest ? "no-apartments" : "check"} />
        </Box>
      </Box>

      <Box position="relative" height="4rem">
        {/* Loading Text */}
        <Flex
          position="absolute"
          top={0}
          left={0}
          right={0}
          flexDirection="column"
          gap={2}
          className={`${styles.fadeTransition} ${
            spinnerVisible ? styles.visible : styles.hidden
          }`}
          style={{ transitionDuration: `${transitionDuration}ms` }}
        >
          <Typography variant="body" weight="medium">
            You're all set!
          </Typography>
          <Typography variant="body" weight="medium" color="muted">
            We’re searching for your perfect home
          </Typography>
        </Flex>

        {/* Success Text */}
        <Flex
          position="absolute"
          top={0}
          left={0}
          right={0}
          flexDirection="column"
          className={`${styles.fadeTransition} ${
            contentVisible ? styles.visible : styles.hidden
          }`}
          style={{ transitionDuration: `${transitionDuration}ms` }}
        >
          <Typography variant="body" weight="medium">
            {isClosest ? (
              <>
                <p>No available apartments matched your search</p>
                <br />
                We've curated a list of the closest matches to what you're
                looking for
              </>
            ) : (
              `We've found ${totalFound} apartments that we think you will love!`
            )}
          </Typography>
        </Flex>
      </Box>
    </Flex>
  );
};
